import {
    type ReactNode,
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    NCoreUIKitTheme,
    Switch,
    Text,
    Kbd
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    type DocsStyleTokens,
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";

const NAME = "Kbd";

const USAGE_CODE = `<Kbd
    keys={["mod", "K"]}
/>

<Kbd
    keys="mod+shift+E"
    size="medium"
/>

<Kbd
    separator="plus"
    keys="ctrl+K"
/>`;

const KEY_EXAMPLES: Array<{
    keys: string | Array<string>;
    label: string;
}> = [
    {
        label: "[\"mod\", \"K\"]",
        keys: [
            "mod",
            "K"
        ]
    },
    {
        label: "\"mod+shift+E\"",
        keys: "mod+shift+E"
    },
    {
        label: "\"alt+enter\"",
        keys: "alt+enter"
    },
    {
        label: "\"escape\"",
        keys: "escape"
    },
    {
        label: "\"mod++\"",
        keys: "mod++"
    },
    {
        label: "\"F5\"",
        keys: "F5"
    }
];

const SEPARATORS = [
    "auto",
    "plus",
    "none"
] as const;

const BEHAVIOURS = [
    "always",
    "auto"
] as const;

const SIZES = [
    "small",
    "medium"
] as const;

const getRowStyle = ({
    spaces
}: DocsStyleTokens) => ({
    justifyContent: "space-between",
    gap: spaces.spacingMd,
    alignItems: "center",
    display: "flex"
}) as const;

const getInverseStyle = ({
    radiuses,
    colors,
    spaces
}: DocsStyleTokens) => ({
    backgroundColor: colors.content.container.inverse,
    paddingInline: spaces.spacingSm,
    paddingBlock: spaces.spacingXs,
    borderRadius: radiuses.form
}) as const;

const SampleCell = ({
    children,
    label
}: {
    children: ReactNode;
    label: string;
}) => {
    const {
        controlHeights,
        spaces
    } = NCoreUIKitTheme.useContext();

    return <div
        style={{
            flexDirection: "column",
            gap: spaces.spacingSm,
            alignItems: "center",
            display: "flex"
        }}
    >
        <div
            style={{
                height: controlHeights.small,
                justifyContent: "center",
                alignItems: "center",
                display: "flex"
            }}
        >
            {children}
        </div>
        <Text
            variant="bodySmallSize"
            color="low"
        >
            {label}
        </Text>
    </div>;
};

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("kbdPage.keysTitle"),
        id: "keys"
    },
    {
        title: t("kbdPage.platformTitle"),
        id: "platform"
    },
    {
        title: t("section.sizes"),
        id: "sizes"
    },
    {
        title: t("section.states"),
        id: "states"
    },
    {
        title: t("kbdPage.visibilityTitle"),
        id: "visibility"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const KbdPage = () => {
    const docsTokens = NCoreUIKitTheme.useContext();

    const {
        t
    } = useDocsI18n();

    const [
        isApple,
        setIsApple
    ] = useState(true);

    return <Article>
        <ComponentHeader
            lead={t("kbdPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("kbdPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("kbdPage.overviewCaption")}
                isStacked={true}
            >
                <div
                    style={getRowStyle(docsTokens)}
                >
                    <Text>
                        {t("sample.shortcut")}
                    </Text>
                    <Kbd
                        displayBehaviour="always"
                        keys={[
                            "mod",
                            "K"
                        ]}
                    />
                </div>
                <div
                    style={getRowStyle(docsTokens)}
                >
                    <Text>
                        {t("kbdPage.save")}
                    </Text>
                    <Kbd
                        displayBehaviour="always"
                        keys="mod+S"
                    />
                </div>
                <div
                    style={getRowStyle(docsTokens)}
                >
                    <Text>
                        {t("kbdPage.close")}
                    </Text>
                    <Kbd
                        displayBehaviour="always"
                        keys="escape"
                    />
                </div>
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("kbdPage.keysTitle")}
            id="keys"
        >
            <Paragraph>
                {t("kbdPage.keysText")}
            </Paragraph>
            <Preview>
                {
                    KEY_EXAMPLES.map(({
                        label,
                        keys
                    }) => <SampleCell
                        label={label}
                        key={label}
                    >
                        <Kbd
                            displayBehaviour="always"
                            keys={keys}
                        />
                    </SampleCell>)
                }
            </Preview>
        </Section>
        <Section
            title={t("kbdPage.platformTitle")}
            id="platform"
        >
            <Paragraph>
                {t("kbdPage.platformText")}
            </Paragraph>
            <Preview
                caption={t("kbdPage.platformCaption")}
                isStacked={true}
            >
                <Switch
                    title={t("kbdPage.platformSwitch")}
                    onCheckedChange={setIsApple}
                    isChecked={isApple}
                />
                <div
                    style={getRowStyle(docsTokens)}
                >
                    {
                        SEPARATORS.map((separator) => <SampleCell
                            label={`separator="${separator}"`}
                            key={separator}
                        >
                            <Kbd
                                platform={isApple ? "apple" : "other"}
                                displayBehaviour="always"
                                separator={separator}
                                keys="mod+shift+K"
                            />
                        </SampleCell>)
                    }
                </div>
            </Preview>
        </Section>
        <Section
            title={t("section.sizes")}
            id="sizes"
        >
            <Paragraph>
                {t("kbdPage.sizesText")}
            </Paragraph>
            <Preview>
                {
                    SIZES.map((size) => <SampleCell
                        label={size}
                        key={size}
                    >
                        <Kbd
                            displayBehaviour="always"
                            keys="mod+K"
                            size={size}
                        />
                    </SampleCell>)
                }
            </Preview>
        </Section>
        <Section
            title={t("section.states")}
            id="states"
        >
            <Paragraph>
                {t("kbdPage.statesText")}
            </Paragraph>
            <Preview>
                <SampleCell
                    label="isDisabled"
                >
                    <Kbd
                        displayBehaviour="always"
                        isDisabled={true}
                        keys="mod+K"
                    />
                </SampleCell>
                <SampleCell
                    label="isInField"
                >
                    <Kbd
                        displayBehaviour="always"
                        isInField={true}
                        keys="mod+K"
                    />
                </SampleCell>
                <SampleCell
                    label="isInverse"
                >
                    <div
                        style={getInverseStyle(docsTokens)}
                    >
                        <Kbd
                            displayBehaviour="always"
                            isInverse={true}
                            keys="mod+K"
                        />
                    </div>
                </SampleCell>
            </Preview>
        </Section>
        <Section
            title={t("kbdPage.visibilityTitle")}
            id="visibility"
        >
            <Paragraph>
                {t("kbdPage.visibilityText")}
            </Paragraph>
            <Preview
                caption={t("kbdPage.visibilityCaption")}
            >
                {
                    BEHAVIOURS.map((behaviour) => <SampleCell
                        label={`displayBehaviour="${behaviour}"`}
                        key={behaviour}
                    >
                        <Kbd
                            displayBehaviour={behaviour}
                            keys="mod+K"
                        />
                    </SampleCell>)
                }
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("kbdPage.a11yPresentational"),
                    t("kbdPage.a11yNames"),
                    t("kbdPage.a11yNative"),
                    t("kbdPage.a11yLocale"),
                    t("kbdPage.a11yHidden")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("kbdPage.propKeys"),
                        type: "string | Array<string>",
                        name: "keys"
                    },
                    {
                        description: t("kbdPage.propDisplayBehaviour"),
                        type: "\"always\" | \"auto\"",
                        name: "displayBehaviour",
                        defaultValue: "\"auto\""
                    },
                    {
                        description: t("kbdPage.propSeparator"),
                        type: "\"plus\" | \"none\" | \"auto\"",
                        defaultValue: "\"auto\"",
                        name: "separator"
                    },
                    {
                        description: t("kbdPage.propPlatform"),
                        type: "\"apple\" | \"other\"",
                        name: "platform"
                    },
                    {
                        description: t("kbdPage.propSize"),
                        type: "\"small\" | \"medium\"",
                        defaultValue: "\"small\"",
                        name: "size"
                    },
                    {
                        description: t("kbdPage.propVariant"),
                        type: "\"outline\" | \"ghost\"",
                        defaultValue: "\"outline\"",
                        name: "variant"
                    },
                    {
                        description: t("kbdPage.propIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        description: t("kbdPage.propIsInverse"),
                        defaultValue: "false",
                        name: "isInverse",
                        type: "boolean"
                    },
                    {
                        description: t("kbdPage.propIsInField"),
                        defaultValue: "false",
                        name: "isInField",
                        type: "boolean"
                    },
                    {
                        description: t("kbdPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("kbdPage.propCustomTheme"),
                        name: "customTheme"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("kbdPage.propsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default KbdPage;
